import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

interface Upload {
  id: string;
  filename: string;
  status: string;
  created_at: string;
  marker_count?: number;
}

export interface ExtractedMarker {
  key: string;
  name: string;
  value: number;
  unit: string;
  category: string;
  ref_low?: number;
  ref_high?: number;
  optimal_low?: number;
  optimal_high?: number;
  status: 'optimal' | 'below_optimal' | 'above_optimal' | 'critical_low' | 'critical_high';
}

export interface FlaggedMarker extends ExtractedMarker {
  research_context: string;
  pmids: string[];
}

export interface BloodworkResult {
  id: string;
  upload_id: string;
  extracted_markers: ExtractedMarker[];
  flagged_markers: FlaggedMarker[];
  category_scores: Record<string, number>;
  summary: string;
  tier: 1 | 2 | 3;
  lab_date: string;
  lab_provider: string;
  status: string;
  processed_at: string;
}

export type RecommendationType = 'supplement' | 'lifestyle' | 'clinical' | 'retest';

export interface Recommendation {
  id: string;
  type: RecommendationType;
  product_name?: string;
  product_url?: string;
  affiliate_partner?: string;
  reason: string;
  pmids: string[];
  priority: number;
  price_range?: string;
  icon?: string;
  retest_timeline?: string;
}

export function useUploads() {
  return useQuery<{ uploads: Upload[] }>({
    queryKey: ['mr-uploads'],
    queryFn: () => api.get('/bloodwork/uploads'),
  });
}

export function useUploadResults(uploadId: string | undefined) {
  return useQuery<{ result: BloodworkResult }>({
    queryKey: ['mr-results', uploadId],
    queryFn: () => api.get<{ result: BloodworkResult }>(`/bloodwork/results/${uploadId}`),
    enabled: !!uploadId,
  });
}

export function useUploadBloodwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      file_data: string;
      file_type: 'pdf' | 'image';
      lab_date: string;
      lab_provider: string;
      fasting: boolean;
    }) =>
      api.post<{ uploadId: string; token: string }>('/bloodwork/upload', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mr-uploads'] });
    },
  });
}

export function useRecommendations(uploadId: string | undefined) {
  return useQuery<{ recommendations: Recommendation[] }>({
    queryKey: ['mr-recs', uploadId],
    queryFn: () => api.get<{ recommendations: Recommendation[] }>(`/bloodwork/results/${uploadId}/recommendations`),
    enabled: !!uploadId,
  });
}
